import { jsxs, jsx } from 'react/jsx-runtime';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button.js';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command.js';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover.js';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import '../ui/badge.js';
import 'react-day-picker';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import '@radix-ui/react-dialog';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '@radix-ui/react-progress';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';
import '@clerk/clerk-react';
import '../../lib/toast.js';
import 'react-router-dom';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import './RichTextEditor.js';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';

/**
 * A paginated combobox component with search functionality
 * Supports server-side pagination and search
 */
function PaginatedCombobox({ getData, getValueFromItem, getLabelFromItem, value, onValueChange, initialItem, placeholder = 'Select item...', searchPlaceholder = 'Search...', emptyMessage = 'No items found.', pageSize = 20, className, disabled = false, }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedItem, setSelectedItem] = useState(initialItem);
    const scrollRef = useRef(null);
    const loadMoreRef = useRef(null);
    /**
     * Sync selectedItem when initialItem changes (e.g., form data finishes loading)
     */
    useEffect(() => {
        if (initialItem) {
            setSelectedItem(initialItem);
        }
    }, [initialItem]);
    /**
     * Fetch data from the server
     */
    const fetchData = useCallback(async (pageNum, searchQuery, append = false) => {
        setLoading(true);
        try {
            const response = await getData({
                page: pageNum,
                limit: pageSize,
                search: searchQuery || undefined,
            });
            if (append) {
                setItems(prev => [...prev, ...response.data]);
            }
            else {
                setItems(response.data);
            }
            setHasMore(response.pagination.hasNext);
            setPage(pageNum);
        }
        catch (error) {
            console.error('Error fetching data:', error);
            setItems([]);
            setHasMore(false);
        }
        finally {
            setLoading(false);
        }
    }, [getData, pageSize]);
    /**
     * Load initial data when the combobox opens
     */
    useEffect(() => {
        if (open && items.length === 0) {
            fetchData(1, search);
        }
    }, [open, items.length, search, fetchData]);
    /**
     * Handle search input changes with debouncing
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            if (open) {
                fetchData(1, search);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search, open, fetchData]);
    /**
     * Set up intersection observer for infinite scroll
     */
    useEffect(() => {
        const currentLoadMoreRef = loadMoreRef.current;
        if (!currentLoadMoreRef || !open)
            return;
        const observer = new IntersectionObserver(entries => {
            const [entry] = entries;
            console.log('Intersection observed:', {
                isIntersecting: entry.isIntersecting,
                hasMore,
                loading,
                page,
            });
            if (entry.isIntersecting && hasMore && !loading) {
                console.log('Loading more items...');
                fetchData(page + 1, search, true);
            }
        }, { threshold: 0, rootMargin: '100px' });
        observer.observe(currentLoadMoreRef);
        console.log('Observer set up, hasMore:', hasMore);
        return () => {
            observer.disconnect();
        };
    }, [open, hasMore, loading, page, search, fetchData]);
    /**
     * Handle item selection
     */
    const handleSelect = useCallback((currentValue) => {
        const newValue = currentValue === value ? '' : currentValue;
        const item = items.find(item => getValueFromItem(item) === currentValue);
        setSelectedItem(item);
        onValueChange?.(newValue, item);
        setOpen(false);
    }, [value, items, getValueFromItem, onValueChange]);
    /**
     * Get display label for selected value
     */
    const getDisplayLabel = useCallback(() => {
        if (!value)
            return placeholder;
        // Use cached selected item if available
        if (selectedItem && getValueFromItem(selectedItem) === value) {
            return getLabelFromItem(selectedItem);
        }
        // Try to find in current items
        const item = items.find(item => getValueFromItem(item) === value);
        if (item) {
            setSelectedItem(item);
            return getLabelFromItem(item);
        }
        return placeholder;
    }, [value, selectedItem, items, placeholder, getValueFromItem, getLabelFromItem]);
    return (jsxs(Popover, { open: open, onOpenChange: setOpen, children: [jsx(PopoverTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", role: "combobox", "aria-expanded": open, className: cn('w-full justify-between', className), disabled: disabled, children: [jsx("span", { className: "truncate", children: getDisplayLabel() }), jsx(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })] }) }), jsx(PopoverContent, { className: "w-(--radix-popover-trigger-width) p-0", align: "start", side: "bottom", sideOffset: 4, avoidCollisions: false, children: jsxs(Command, { shouldFilter: false, className: "h-auto", children: [jsx(CommandInput, { placeholder: searchPlaceholder, className: "h-9", value: search, onValueChange: setSearch }), jsxs(CommandList, { ref: scrollRef, children: [!loading && items.length === 0 && jsx(CommandEmpty, { children: emptyMessage }), jsxs(CommandGroup, { children: [items.map(item => {
                                            const itemValue = getValueFromItem(item);
                                            const itemLabel = getLabelFromItem(item);
                                            return (jsxs(CommandItem, { value: itemValue, onSelect: handleSelect, className: "cursor-pointer", children: [jsx("span", { className: "truncate", children: itemLabel }), jsx(Check, { className: cn('ml-auto h-4 w-4 shrink-0', value === itemValue ? 'opacity-100' : 'opacity-0') })] }, itemValue));
                                        }), hasMore && (jsx("div", { ref: loadMoreRef, className: "flex items-center justify-center py-3 border-t", children: loading && jsx(Loader2, { className: "h-4 w-4 animate-spin text-muted-foreground" }) }))] }), loading && items.length === 0 && (jsx("div", { className: "flex items-center justify-center py-6", children: jsx(Loader2, { className: "h-4 w-4 animate-spin" }) }))] })] }) })] }));
}

export { PaginatedCombobox };
//# sourceMappingURL=PaginatedCombobox.js.map
